/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ResponseType } from '@microsoft/microsoft-graph-client';
import { prepScopes } from '../utils/GraphHelpers';
import { blobToBase64 } from '../utils/Utils';
import { findContactByEmail, findUserByEmail, getEmailFromGraphEntity } from './graph.people';
/**
 * retrieves a photo for the specified resource.
 *
 * @param {string} resource
 * @param {string[]} scopes
 * @returns {Promise<string>}
 */
function getPhotoForResource(graph, resource, scopes) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const blob = yield graph
                .api(`${resource}/photo/$value`)
                .responseType(ResponseType.BLOB)
                .middlewareOptions(prepScopes(...scopes))
                .get();
            return yield blobToBase64(blob);
        }
        catch (e) {
            return null;
        }
    });
}
/**
 * async promise, returns Graph photos associated with contacts of the logged in user
 * @param contactId
 * @returns {Promise<string>}
 * @memberof Graph
 */
export function getContactPhoto(graph, contactId) {
    return getPhotoForResource(graph, `me/contacts/${contactId}`, ['contacts.read']);
}
/**
 * async promise, returns Graph photo associated with provided userId
 * @param userId
 * @returns {Promise<string>}
 * @memberof Graph
 */
export function getUserPhoto(graph, userId) {
    return getPhotoForResource(graph, `users/${userId}`, ['user.readbasic.all']);
}
/**
 * async promise, returns Graph photo associated with the logged in user
 * @returns {Promise<string>}
 * @memberof Graph
 */
export function myPhoto(graph) {
    return getPhotoForResource(graph, 'me', ['user.read']);
}
/**
 * async promise, loads image of user
 *
 * @export
 */
export function getPersonImage(graph, person) {
    return __awaiter(this, void 0, void 0, function* () {
        let image;
        let email;
        if (person.userPrincipalName) {
            // try to find a user by userPrincipalName
            const userPrincipalName = person.userPrincipalName;
            image = yield getUserPhoto(graph, userPrincipalName);
        }
        else if (person.personType.subclass === 'PersonalContact') {
            // if person is a contact, look for them and their photo in contact api
            email = getEmailFromGraphEntity(person);
            const contact = yield findContactByEmail(graph, email);
            if (contact && contact.length && contact[0].id) {
                image = yield getContactPhoto(graph, contact[0].id);
            }
        }
        else if (person.id) {
            image = yield getUserPhoto(graph, person.id);
        }
        if (image) {
            return image;
        }
        // try to find a user by e-mail
        email = getEmailFromGraphEntity(person);
        if (email) {
            const users = yield findUserByEmail(graph, email);
            if (users && users.length) {
                // Check for an OrganizationUser
                const orgUser = users.find(p => {
                    return p.personType && p.personType.subclass === 'OrganizationUser';
                });
                if (orgUser) {
                    // Lookup by userId
                    const userId = users[0].scoredEmailAddresses[0].address;
                    image = yield getUserPhoto(graph, userId);
                }
                else {
                    // Lookup by contactId
                    for (const user of users) {
                        const contactId = user.id;
                        image = yield getContactPhoto(graph, contactId);
                        if (image) {
                            break;
                        }
                    }
                }
            }
        }
        return image;
    });
}
//# sourceMappingURL=graph.photos.js.map